<template>
  <AvisOFModal
      v-if="avisOFUrl"
      :candidateUrl="avisOFUrl"
      :ofObjId="ofObjId"
      :userAccountObjId="userAccountObjId"
      :meetingId="meeting.id"
      @close="avisOFUrl = null"
  />
  <div class="meeting-form-overlay">
    <div class="meeting-form-modal">
      <div class="meeting-form-header">
        <div>
          <h3>Grille d'entretien</h3>
        </div>

        <div class="header-right">
          <div class="header-meta">
            <span v-if="isSavingForm" class="saving-indicator">Enregistrement...</span>
            <span v-else>
              <span v-if="candidateFullName" class="meeting-form-candidate">Candidat : {{ candidateFullName }}</span>
              <br>
              <span class="meeting-form-subtitle">{{ meetingTitle }}</span>
            </span>
          </div>

          <div v-if="isPdfPreviewMode" class="meeting-form-header-actions">
            <button
                class="header-action-btn"
                :disabled="isDownloadingPdf"
                type="button"
                @click="downloadPdf"
            >
              {{ isDownloadingPdf ? 'Téléchargement...' : 'Télécharger le pdf' }}
            </button>
            <button
                class="header-action-btn eva-export-btn"
                :class="evaExportButtonClass"
                :disabled="isExportingEva"
                type="button"
                @click="exportToEva"
            >
              {{ evaExportButtonLabel }}
            </button>
          </div>

          <button class="close-btn" type="button" @click="close">&times;</button>
        </div>
      </div>

      <div v-if="errorMessage" class="meeting-form-error">
        {{ errorMessage }}
      </div>

      <div v-if="isPdfPreviewMode" class="meeting-form-pdf-body">
        <div v-if="isPdfPreviewLoading" class="meeting-form-pdf-status">
          Chargement de l'aperçu PDF...
        </div>

        <div v-if="isPdfPreviewError" class="meeting-form-pdf-status meeting-form-pdf-status-error">
          Impossible de charger l'aperçu PDF.
        </div>

        <iframe
            v-show="!isPdfPreviewError"
            :key="pdfBlobUrl"
            :src="pdfBlobUrl"
            class="meeting-form-pdf-iframe"
            title="Aperçu PDF de la grille d'entretien"
            @load="onPdfIframeLoad"
            @error="onPdfIframeError"
        />
      </div>

      <template v-else>
        <div v-if="sections.length === 0" class="meeting-form-empty">
          Aucune section de formulaire disponible.
        </div>

        <div v-else class="meeting-form-body">
          <section v-for="section in sections" :key="section.key" class="section-card">
            <h4>{{ section.label }}</h4>
            <h6 class="section-annotation" v-if="section.annotation">{{ section.annotation }}</h6>
            <div class="questions-grid">
              <article v-for="question in section.questions" :key="question.key" class="question-card">
                <div class="question-header">
                  <p class="question-label">{{ question.label }}</p>
                </div>

                <div v-if="question.type === 'checkbox'" class="checkbox-options">
                  <label v-for="answer in question.answers" :key="answer" class="checkbox-option">
                    <input
                        :checked="isAnswerChecked(question, answer)"
                        :disabled="isQuestionReadOnly(question)"
                        type="checkbox"
                        @change="toggleCheckbox(section.key, question.key, answer, $event.target.checked)"
                    />
                    <span>{{ answer }}</span>
                  </label>
                  <textarea
                      v-if="question.answers.length === 0"
                      :value="getQuestionInputValue(section.key, question)"
                      :placeholder="getQuestionPlaceHolder(section.key, question)"
                      :disabled="isQuestionReadOnly(question)"
                      class="response-textarea"
                      rows="3"
                      @input="onTextInput(section.key, question.key, $event.target.value)"
                  />
                </div>

                <div v-else-if="question.type === 'radio'" class="checkbox-options">
                  <label v-for="answer in question.answers" :key="answer" class="checkbox-option">
                    <input
                        :checked="isAnswerChecked(question, answer)"
                        :disabled="isQuestionReadOnly(question)"
                        :name="`radio-${section.key}-${question.key}`"
                        type="radio"
                        @change="toggleRadio(section.key, question.key, answer, $event.target.checked)"
                    />
                    <span>{{ answer }}</span>
                  </label>
                </div>

                <SearchInput
                    v-else-if="isPersonInChargeQuestion(question)"
                    v-model="managerSearchTerm"
                    :clear-button-title="UI_MESSAGES.CLEAR_BUTTON_TITLE"
                    :has-value="!!(selectedInterviewManagerObjId && selectedInterviewManagerObjId > 0)"
                    :loading="managerSearchLoading"
                    :placeholder="(selectedInterviewManagerObjId && selectedInterviewManagerObjId > 0) ? UI_MESSAGES.SEARCH_MANAGER_PLACEHOLDER_MODIFY : UI_MESSAGES.SEARCH_MANAGER_PLACEHOLDER"
                    :suggestion-key-field="'interviewManagerObjId'"
                    :suggestions="managerSuggestions"
                    @clear="clearSelection"
                    @search="onManagerSearch"
                    @select="selectInterviewManager"
                    @clear-suggestions="clearManagerSuggestions"
                >
              <template #suggestion="{ suggestion }">
                {{ formatPersonDisplayName(suggestion) }}
                <small v-if="suggestion.email"> — {{ suggestion.email }}</small>
              </template>
                </SearchInput>

                <div v-else-if="question.type === 'textarea'" class="textarea-bottom-wrapper">
                  <div class="flex-spacer"></div>
                  <textarea
                      :value="getQuestionInputValue(section.key, question)"
                      :placeholder="getQuestionPlaceHolder(section.key, question)"
                      :disabled="isQuestionReadOnly(question)"
                      class="response-textarea"
                      rows="4"
                      @input="onTextInput(section.key, question.key, $event.target.value)"
                  />
                </div>

                <input
                    v-else
                    :value="getQuestionInputValue(section.key, question)"
                    :placeholder="getQuestionPlaceHolder(section.key, question)"
                    class="response-input"
                    :disabled="isQuestionReadOnly(question)"
                    :type="question.type === 'date' ? 'date' : 'text'"
                    @change="onTextInput(section.key, question.key, $event.target.value)"
                    @input="onTextInput(section.key, question.key, $event.target.value)"
                />
              </article>
            </div>
          </section>
        </div>

        <div class="meeting-form-footer">
          <button
              class="validate-btn"
              :disabled="isSavingForm || isValidatingAndPreviewing"
              type="button"
              @click="validateAndFinish"
          >
            {{ isSavingForm || isValidatingAndPreviewing ? 'Enregistrement...' : 'Valider et terminer' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from 'vue'
import AvisOFModal from './AvisOFModal.vue'
import {useDateFormatter} from '@/composables/useDateFormatter'
import {formatPersonDisplayName, useSearchAutocomplete} from '@/composables/useSearchAutocomplete'
import {usePlanningStore} from '@/store/planningStore'
import SearchInput from '@/components/UI/SearchInput.vue'
import {PLANNING_STATUS, SEARCH_CONFIG, UI_MESSAGES} from '@/constants/planning'

const DEBOUNCE_SAVE_MS = 10000
const EVA_EXPORT_FEEDBACK_MS = 3000
const API_BASE_URL = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '')
const PERSON_IN_CHARGE_KEY = 'personne_chargee'

const props = defineProps({
  meeting: {
    type: Object,
    required: true
  },
  ofObjId: {
    type: [String, Number],
    required: true
  },
  userAccountObjId: {
    type: [String, Number],
    required: true
  },
  planning: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close'])
const planningStore = usePlanningStore()
const {formatDate, formatTime} = useDateFormatter()
const formState = reactive({sections: {}})
const errorState = reactive({message: ''})
const isSavingForm = ref(false)
const hasPendingChanges = ref(false)
const isValidatingAndPreviewing = ref(false)
const isPdfPreviewMode = ref(false)
const isPdfPreviewLoading = ref(false)
const isPdfPreviewError = ref(false)
const pdfPreviewVersion = ref(Date.now())
const pdfBlobUrl = ref('')
const isExportingEva = ref(false)
const evaExportStatus = ref('idle')
const isDownloadingPdf = ref(false)
const avisOFUrl = ref(null)
const selectedInterviewManagerObjId = ref(
    props.planning?.interviewManagerObjId || props.meeting?.planning?.interviewManagerObjId || null
)
let debounceTimerId = null
let evaExportStatusTimerId = null
let isSyncingFromFormResponse = false
let isSelectingInterviewManager = false

const meetingTitle = computed(() => {
  const startAt = props.meeting?.startAt
  const endAt = props.meeting?.endAt
  if (!startAt || !endAt) return 'Edition du formulaire'
  return `${formatDate(startAt)} de ${formatTime(startAt)} a ${formatTime(endAt)}`
})

const fromHtmlDate = (dateStr) => {
  if (!dateStr) return ''
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (match) {
    const [, yyyy, mm, dd] = match
    return `${dd}/${mm}/${yyyy}`
  }
  return dateStr
}

const joinFullName = (firstname, lastname) => {
  return [firstname, lastname].filter(Boolean).join(' ').trim()
}

const candidateFullName = computed(() => {
  const firstname = props.meeting?.candidacy?.firstname || props.meeting?.candidate?.firstname
  const lastname = props.meeting?.candidacy?.lastname || props.meeting?.candidate?.lastname
  return joinFullName(firstname, lastname)
})

const interviewerFullName = computed(() => {
  const firstname = props.planning?.interviewManager?.firstname || props.meeting?.planning?.interviewManager?.firstname
  const lastname =  props.planning?.interviewManager?.lastname || props.meeting?.planning?.interviewManager?.lastname
  return joinFullName(firstname, lastname)
})

const getSearchOfIds = () => {
  if (Array.isArray(props.planning?.ofsIdsEffective) && props.planning.ofsIdsEffective.length > 0) {
    return props.planning.ofsIdsEffective
  }
  return [props.ofObjId]
}

const searchFunction = async (term) => {
  const searchOfIds = getSearchOfIds()
  const results = await Promise.all(
      searchOfIds.map((ofObjId) => {
        return planningStore.searchInterviewManagers(
            ofObjId,
            props.userAccountObjId,
            term
        )
      })
  )

  const allManagers = results.map((res) => {
    if (Array.isArray(res)) {
      return res
    }
    return []
  }).flat()

  const uniqueManagers = []
  const seenIds = new Set()

  for (const manager of allManagers) {
    const id = manager.interviewManagerObjId || manager.id
    if (id && !seenIds.has(id)) {
      seenIds.add(id)
      uniqueManagers.push(manager)
    }
  }

  return uniqueManagers
}

const {
  searchTerm: managerSearchTerm,
  suggestions: managerSuggestions,
  searchLoading: managerSearchLoading,
  onSearch: onManagerSearch,
  clearSuggestions: clearManagerSuggestions,
  clearSearch: clearManagerSearch,
  selectSuggestion
} = useSearchAutocomplete(searchFunction, SEARCH_CONFIG.DEBOUNCE_MS, SEARCH_CONFIG.MIN_LENGTH)

const meetingDateStart = computed(() => {
  return props.meeting.startAt;
})



const getQuestionInputValue = (sectionKey, question) => {
  if (!question) return ''

  const sourceQuestion = getQuestion(sectionKey, question.key) || question

  if (sourceQuestion.type === 'checkbox') {
    return normalizeCheckboxResponse(sourceQuestion.response).join(', ')
  }
  if (sourceQuestion.type === 'date') {
    return toHtmlDate(sourceQuestion.response)
  }

  if (null === sourceQuestion.response) {
    return ''
  }

  return String(sourceQuestion.response)
}


const getQuestionPlaceHolder = (sectionKey, question) => {
  if (!question) return '';

  const sourceQuestion = getQuestion(sectionKey, question.key) || question

  if (sourceQuestion.placeholder) {
    return String(sourceQuestion.placeholder);
  }

  return '';
}


const sections = computed(() => {
  return Object.keys(formState.sections).map((sectionKey) => {
    const sectionValue = formState.sections[sectionKey] || {}
    return {
      key: sectionKey,
      label: sectionValue.label || sectionKey,
      annotation: sectionValue.annotation || null,
      questions: Object.keys(sectionValue.questions || {}).map((questionKey) => {
        const questionValue = sectionValue.questions[questionKey] || {}
        return {
          key: questionKey,
          label: questionValue.label || questionKey,
          type: questionValue.type || 'text',
          answers: Array.isArray(questionValue.answers) ? questionValue.answers : [],
          readonly: questionValue.readonly,
          response: questionValue.response ?? null
        }
      })
    }
  })
})

const errorMessage = computed(() => errorState.message)

const pdfPreviewUrl = computed(() => {
  const meetingId = props.meeting?.id
  if (!meetingId) return ''
  const previewPath = `/api/planning/${props.ofObjId}/${props.userAccountObjId}/meeting-form/preview/${meetingId}`
  return API_BASE_URL ? `${API_BASE_URL}${previewPath}` : previewPath
})

const pdfPreviewIframeUrl = computed(() => {
  if (!pdfPreviewUrl.value) return ''
  const separator = pdfPreviewUrl.value.includes('?') ? '&' : '?'
  return `${pdfPreviewUrl.value}${separator}t=${pdfPreviewVersion.value}`
})

const evaExportButtonLabel = computed(() => {
  if (isExportingEva.value) return 'Export en cours...'
  if (evaExportStatus.value === 'success') return 'Export réussi'
  if (evaExportStatus.value === 'error') return 'Erreur export'
  return 'Exporter vers EVA'
})

const evaExportButtonClass = computed(() => {
  return {
    'is-loading': isExportingEva.value,
    'is-success': evaExportStatus.value === 'success',
    'is-error': evaExportStatus.value === 'error'
  }
})

const clone = (value) => {
  if (!value) return null
  return JSON.parse(JSON.stringify(value))
}

const setFormFromMeeting = () => {
  const meetingForm = props.meeting?.meetingForm?.form
  const cloned = clone(meetingForm)
  formState.sections = cloned?.sections || {}
}

const getQuestion = (sectionKey, questionKey) => {
  return formState.sections?.[sectionKey]?.questions?.[questionKey] || null
}

const findQuestionLocationByKey = (questionKey) => {
  const sections = formState.sections || {}

  for (const sectionKey of Object.keys(sections)) {
    const questions = sections[sectionKey]?.questions || {}
    if (Object.prototype.hasOwnProperty.call(questions, questionKey)) {
      return {sectionKey, questionKey}
    }
  }

  return null
}

const personInChargeResponse = computed(() => {
  const location = findQuestionLocationByKey(PERSON_IN_CHARGE_KEY)
  if (!location) return ''

  const question = getQuestion(location.sectionKey, location.questionKey)
  if (!question || question.response == null) return ''
  return String(question.response)
})

const isPersonInChargeQuestion = (question) => {
  return question?.key === PERSON_IN_CHARGE_KEY
}

const isQuestionReadOnly = (question) => {
  return question?.readonly === true || question?.readonly === 'true'
}

const normalizeCheckboxResponse = (response) => {
  if (response == null || response === '') return []
  if (Array.isArray(response)) return response
  return [response]
}

// Convertit une date vers yyyy-MM-dd (dd/MM/yyyy, yyyy-MM-dd, yyyy-MM-dd HH:mm:ss)
const toHtmlDate = (dateStr) => {
  if (!dateStr) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr
  const dateTimeMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})\s\d{2}:\d{2}:\d{2}$/)
  if (dateTimeMatch) return dateTimeMatch[1]
  const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (match) {
    const [, dd, mm, yyyy] = match
    return `${yyyy}-${mm}-${dd}`
  }
  return dateStr
}

const normalizeResponseForSave = (questionType, response) => {
  if (questionType === 'checkbox') {
    if (response == null || response === '') return []
    if (Array.isArray(response)) return response
    return [response]
  }
  if (questionType === 'radio') {
    if (response == null || response === '') return null
    if (Array.isArray(response)) return response.length > 0 ? response[0] : null
    return response
  }
  if (response === '') return null
  return response
}

const buildPayload = () => {
  const payload = {sections: {}}
  Object.keys(formState.sections).forEach((sectionKey) => {
    payload.sections[sectionKey] = {questions: {}}
    const questions = formState.sections[sectionKey]?.questions || {}
    Object.keys(questions).forEach((questionKey) => {
      const question = questions[questionKey]
      payload.sections[sectionKey].questions[questionKey] = {
        response: normalizeResponseForSave(question?.type || 'text', question?.response ?? null)
      }
    })
  })
  return payload
}

const applyServerResponses = (updatedMeetingForm) => {
  const sectionsMap = updatedMeetingForm?.form?.sections
  if (!sectionsMap || typeof sectionsMap !== 'object') return

  Object.keys(sectionsMap).forEach((sectionKey) => {
    const currentSection = formState.sections?.[sectionKey]
    const serverSection = sectionsMap[sectionKey]
    if (!currentSection || !serverSection) return

    currentSection.label = serverSection.label
    const questions = serverSection.questions || {}
    Object.keys(questions).forEach((questionKey) => {
      const currentQuestion = currentSection?.questions?.[questionKey]
      const serverQuestion = questions[questionKey]
      if (!currentQuestion || !serverQuestion) return

      currentQuestion.label = serverQuestion.label
      currentQuestion.type = serverQuestion.type
      currentQuestion.answers = Array.isArray(serverQuestion.answers) ? serverQuestion.answers : []
      currentQuestion.readonly = serverQuestion.readonly
      currentQuestion.response = serverQuestion.response ?? null
    })
  })

  props.meeting.meetingForm = updatedMeetingForm
}

const saveForm = async () => {
  if (isSavingForm.value) return false
  if (!hasPendingChanges.value) return true

  isSavingForm.value = true
  errorState.message = ''

  try {
    const updatedMeetingForm = await planningStore.updateMeetingForm(
        props.ofObjId,
        props.userAccountObjId,
        props.meeting.id,
        buildPayload()
    )
    applyServerResponses(updatedMeetingForm)
    hasPendingChanges.value = false
    return true
  } catch (err) {
    errorState.message = err?.message || err || 'Erreur lors de la sauvegarde de la grille d\'entretien'
    return false
  } finally {
    isSavingForm.value = false
  }
}

const clearDebounce = () => {
  if (debounceTimerId) {
    clearTimeout(debounceTimerId)
    debounceTimerId = null
  }
}

const clearEvaExportStatusTimeout = () => {
  if (evaExportStatusTimerId) {
    clearTimeout(evaExportStatusTimerId)
    evaExportStatusTimerId = null
  }
}

const setEvaExportStatus = (status) => {
  evaExportStatus.value = status
  clearEvaExportStatusTimeout()

  if (status === 'idle') return

  evaExportStatusTimerId = window.setTimeout(() => {
    evaExportStatus.value = 'idle'
    evaExportStatusTimerId = null
  }, EVA_EXPORT_FEEDBACK_MS)
}

const scheduleAutoSave = () => {
  clearDebounce()
  debounceTimerId = window.setTimeout(() => {
    debounceTimerId = null
    saveForm()
  }, DEBOUNCE_SAVE_MS)
}

const markDirty = () => {
  hasPendingChanges.value = true
  scheduleAutoSave()
}

const onTextInput = (sectionKey, questionKey, value) => {
  const question = getQuestion(sectionKey, questionKey)
  if (!question) return
  const normalizedValue = question.type === 'date' ? fromHtmlDate(value) : value
  if (question.response === normalizedValue) return
  question.response = normalizedValue
  markDirty()
}

const selectInterviewManager = (manager) => {
  isSelectingInterviewManager = true
  const selected = selectSuggestion(manager)
  selectedInterviewManagerObjId.value = selected.interviewManagerObjId || selected.id || null

  const location = findQuestionLocationByKey(PERSON_IN_CHARGE_KEY)
  if (location) {
    onTextInput(
        location.sectionKey,
        location.questionKey,
        formatPersonDisplayName(selected)
    )
  }
  isSelectingInterviewManager = false
}

const clearSelection = () => {
  selectedInterviewManagerObjId.value = null
  clearManagerSearch()
}

const ensurePersonInChargeDefaultValue = () => {
  const location = findQuestionLocationByKey(PERSON_IN_CHARGE_KEY)
  if (!location) return

  const question = getQuestion(location.sectionKey, location.questionKey)
  if (!question) return

  const isMissingResponse = question.response == null
      || (typeof question.response === 'string' && question.response.trim() === '')

  const fallbackValue = interviewerFullName.value
  if (!isMissingResponse || !fallbackValue) return

  onTextInput(location.sectionKey, location.questionKey, fallbackValue)
}

const isAnswerChecked = (question, answer) => {
  if (!question) return false
  if (question.type === 'checkbox') {
    return normalizeCheckboxResponse(question.response).includes(answer)
  }
  return question.response === answer
}

const toggleCheckbox = (sectionKey, questionKey, answer, checked) => {
  const question = getQuestion(sectionKey, questionKey)
  if (!question) return
  if (isQuestionReadOnly(question)) return

  const selected = normalizeCheckboxResponse(question.response)
  if (checked) {
    if (!selected.includes(answer)) selected.push(answer)
  } else {
    question.response = selected.filter((value) => value !== answer)
    markDirty()
    return
  }
  question.response = selected
  markDirty()
}

const toggleRadio = (sectionKey, questionKey, answer, checked) => {
  const question = getQuestion(sectionKey, questionKey)
  if (!question) return
  if (isQuestionReadOnly(question)) return
  question.response = checked ? answer : null
  markDirty()
}

const validateAndFinish = async () => {
  if (isValidatingAndPreviewing.value) return

  isValidatingAndPreviewing.value = true
  clearDebounce()

  try {
    const isSaved = await saveForm()
    if (!isSaved) return

    setEvaExportStatus('idle')
    pdfPreviewVersion.value = Date.now()
    isPdfPreviewMode.value = true
    isPdfPreviewLoading.value = true
    isPdfPreviewError.value = false

    try {
      const response = await planningStore.getMeetingFormPreviewPdf(
          props.ofObjId,
          props.userAccountObjId,
          props.meeting.id
      )
      const blob = response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data], {type: 'application/pdf'})

      if (pdfBlobUrl.value) {
        window.URL.revokeObjectURL(pdfBlobUrl.value)
      }
      pdfBlobUrl.value = window.URL.createObjectURL(blob)
    } catch (err) {
      isPdfPreviewLoading.value = false
      isPdfPreviewError.value = true
    }
  } finally {
    isValidatingAndPreviewing.value = false
  }
}

const onPdfIframeLoad = () => {
  isPdfPreviewLoading.value = false
  isPdfPreviewError.value = false
}

const onPdfIframeError = () => {
  isPdfPreviewLoading.value = false
  isPdfPreviewError.value = true
}

const extractFilenameFromContentDisposition = (contentDisposition) => {
  if (typeof contentDisposition !== 'string' || contentDisposition.trim() === '') return ''

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/^["']|["']$/g, '')
  }

  const asciiMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i)
  if (asciiMatch?.[1]) return asciiMatch[1].trim()

  return ''
}

const sanitizeFilename = (filename, fallbackMeetingId) => {
  if (typeof filename === 'string' && filename.trim() !== '') {
    const cleaned = filename.replace(/[\\/:*?"<>|]/g, '_').trim()
    if (cleaned) return cleaned
  }

  return `grille-entretien-${fallbackMeetingId}.pdf`
}

const triggerFileDownload = (blob, filename) => {
  const blobUrl = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = blobUrl
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0)
}

const downloadPdf = async () => {
  if (isDownloadingPdf.value || !props.meeting?.id) return

  isDownloadingPdf.value = true

  try {
    const response = await planningStore.getMeetingFormPreviewPdf(
        props.ofObjId,
        props.userAccountObjId,
        props.meeting.id
    )

    const blob = response?.data instanceof Blob
        ? response.data
        : new Blob([response?.data], {type: 'application/pdf'})
    const contentDisposition = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition']
    const extractedFilename = extractFilenameFromContentDisposition(contentDisposition)
    const filename = sanitizeFilename(extractedFilename, props.meeting.id)

    triggerFileDownload(blob, filename)
  } catch (err) {
    errorState.message = err?.message || err || 'Erreur lors du téléchargement du PDF'
  } finally {
    isDownloadingPdf.value = false
  }
}

const exportToEva = async () => {
  if (isExportingEva.value) return

  isExportingEva.value = true

  try {
    await planningStore.exportMeetingFormToEva(
        props.ofObjId,
        props.userAccountObjId,
        props.meeting.id
    )
    setEvaExportStatus('success')
    props.meeting.statut = PLANNING_STATUS.COMPLETED;

    try {
      const detail = await planningStore.fetchCandidacyDetails(
          props.ofObjId,
          props.userAccountObjId,
          props.meeting.id
      )
      const candidateUrl = detail?.['Fiche candidat']?.url
      if (candidateUrl) {
        avisOFUrl.value = candidateUrl
      }
    } catch (err) {
      console.error('Impossible d\'ouvrir la fiche candidat après export EVA:', err)
    }
  } catch (err) {
    errorState.message = err?.message || err || 'Erreur lors de l\'export vers EVA'
    setEvaExportStatus('error')
  } finally {
    isExportingEva.value = false
  }
}

const close = async () => {
  clearDebounce()
  await saveForm()
  emit('close')
}

const onKeydown = (event) => {
  if (event.key === 'Escape') {
    close()
  }
}

onMounted(() => {
  setFormFromMeeting()
  ensurePersonInChargeDefaultValue()
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  clearDebounce()
  clearEvaExportStatusTimeout()
  window.removeEventListener('keydown', onKeydown)
  if (pdfBlobUrl.value) {
    window.URL.revokeObjectURL(pdfBlobUrl.value)
  }
})

watch(() => props.meeting?.meetingForm, () => {
  if (!hasPendingChanges.value && !isSavingForm.value) {
    setFormFromMeeting()
    ensurePersonInChargeDefaultValue()
  }
})

watch(personInChargeResponse, (value) => {
  const planningManagerName = interviewerFullName.value
  if (selectedInterviewManagerObjId.value && planningManagerName && value !== planningManagerName) {
    selectedInterviewManagerObjId.value = null
  }

  if (managerSearchTerm.value === value) return
  isSyncingFromFormResponse = true
  managerSearchTerm.value = value
  isSyncingFromFormResponse = false
}, {immediate: true})

watch(managerSearchTerm, (value) => {
  if (isSyncingFromFormResponse || isSelectingInterviewManager) return

  const location = findQuestionLocationByKey(PERSON_IN_CHARGE_KEY)
  if (!location) return

  const question = getQuestion(location.sectionKey, location.questionKey)
  const currentValue = question?.response == null ? '' : String(question.response)
  if (currentValue === value) return

  selectedInterviewManagerObjId.value = null
  onTextInput(location.sectionKey, location.questionKey, value)
})
</script>


<style>

input:disabled,
select:disabled,
textarea:disabled,
button:disabled {
  background-color: #f5f5f5 !important;
  color: var(--text);
  cursor: not-allowed !important;
  opacity: 1 !important;
  border: 1px solid #e0e0e0 !important;
}

.response-input, .planning .form-control   {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.9rem;
  color: #1f2937;
  background: #fff;
  outline: none;
  height: 42px;
}

.response-input:focus {
  border-color: #38a3a5;
  box-shadow: 0 0 0 2px rgba(56, 163, 165, 0.16);
}

</style>

<style scoped>
.meeting-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(9, 18, 32, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1600;
  padding: 12px;
}

.meeting-form-modal {
  width: calc(95vw - 24px);
  max-width: 1800px;
  height: calc(100vh - 24px);
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.25);
}

.meeting-form-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  color: #ffffff;
  background: var(--primary, #1f5f97);
}

.meeting-form-header h3 {
  margin: 0;
  font-size: 1.75rem;
  color: #ffffff;
}

.meeting-form-candidate {
  margin: 6px 0 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
}

.meeting-form-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.92);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
}

.header-meta {
  text-align: right;
}

.meeting-form-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-action-btn {
  border: 1px solid rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.header-action-btn:hover,
.header-action-btn:focus {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.95);
  outline: none;
}

.header-action-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.eva-export-btn.is-loading {
  border-color: #fbbf24;
  background: rgba(251, 191, 36, 0.22);
}

.eva-export-btn.is-success {
  border-color: #34d399;
  background: rgba(16, 185, 129, 0.2);
}

.eva-export-btn.is-error {
  border-color: #f87171;
  background: rgba(248, 113, 113, 0.22);
}

.close-btn {
  border: none;
  background: transparent;
  color: #fff;
  font-size: 2.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px;
  margin-left: 8px;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover,
.close-btn:focus {
  background: rgba(255, 255, 255, 0.18);
  color: #e63946;
  outline: none;
}

.meeting-form-error {
  background: #fef2f2;
  color: #b91c1c;
  border-bottom: 1px solid #fecaca;
  padding: 10px 16px;
  font-size: 0.9rem;
}

.meeting-form-empty {
  flex: 1;
  padding: 24px;
  color: #4a5568;
  background: #f6f8fb;
}

.meeting-form-body {
  flex: 1;
  overflow: auto;
  padding: 18px 20px 24px;
  background: #f6f8fb;
}

.section-card {
  background: #fff;
  border: 1px solid #d8e0ea;
  border-radius: 10px;
  padding: 14px;
}

.section-annotation{
  font-size: 0.8rem;
  color: #74570D;
  background: #FFF3CD;
  padding: 2px 4px;
}

.section-card + .section-card {
  margin-top: 14px;
}

.section-card h4 {
  margin: 0 0 12px;
  font-size: 1.02rem;
  color: #184e77;
}

.questions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 10px;
}

.question-card {
  border: 1px solid #e7edf5;
  border-radius: 8px;
  padding: 10px;
  background: #fcfdff;
  position: relative;
  display: flex;
  flex-direction: column;
}

.textarea-bottom-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  justify-content: flex-end;
  position: static;
  padding: 0;
  margin: 0;
}

.question-header {
  z-index: 1;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.question-label {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 600;
  color: #2d3748;
}

.saving-indicator {
  font-size: 0.75rem;
  color: #ffffff;
  white-space: nowrap;
}



.response-textarea {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.9rem;
  color: #1f2937;
  background: #fff;
  outline: none;
  resize: vertical;
}

.response-textarea:focus {
  border-color: #38a3a5;
  box-shadow: 0 0 0 2px rgba(56, 163, 165, 0.16);
}

.checkbox-options {
  display: grid;
  gap: 6px;
}

.checkbox-option {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  font-size: 0.9rem;
}

.meeting-form-footer {
  border-top: 1px solid #d8e0ea;
  background: #ffffff;
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
}

.validate-btn {
  border: none;
  border-radius: 8px;
  background: #1f5f97;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.validate-btn:hover,
.validate-btn:focus {
  background: #174c79;
  outline: none;
}

.validate-btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.meeting-form-pdf-body {
  flex: 1;
  position: relative;
  background: #eef2f7;
  min-height: 0;
}

.meeting-form-pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  background: #ffffff;
}

.meeting-form-pdf-status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.95rem;
  color: #1f2937;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 8px;
  border: 1px solid #d8e0ea;
  z-index: 2;
}

.meeting-form-pdf-status-error {
  color: #b91c1c;
  border-color: #fecaca;
  background: rgba(254, 242, 242, 0.96);
}

@media (max-width: 768px) {
  .meeting-form-overlay {
    padding: 8px;
  }

  .meeting-form-modal {
    width: calc(100vw - 16px);
    height: calc(100vh - 16px);
  }

  .questions-grid {
    grid-template-columns: 1fr;
  }

  .meeting-form-header-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}

@media (max-width: 576px) {
  .meeting-form-modal {
    border-radius: 10px;
  }

  .meeting-form-header {
    padding: 12px;
    gap: 10px;
    flex-direction: column;
  }

  .meeting-form-header h3 {
    font-size: 1.06rem;
  }

  .meeting-form-candidate {
    font-size: 0.88rem;
    margin-top: 4px;
  }

  .meeting-form-subtitle {
    font-size: 0.78rem;
    line-height: 1.3;
  }

  .header-right {
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
  }

  .header-meta {
    text-align: left;
  }

  .meeting-form-header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .header-action-btn {
    font-size: 0.78rem;
    padding: 7px 10px;
  }

  .meeting-form-body {
    padding: 12px;
  }

  .meeting-form-footer {
    padding: 10px 12px;
  }
}
</style>
